import { BPFormProvider, Input, Select, IOption } from '@bpba/form-components';
import { VStack, Button, HStack } from '@bpba/ui-components';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SignUpHomeRules } from './SignUpHomeRules';
import useAxios from 'axios-hooks';
import { UrlsBack, UrlsFront } from '../constants/urls';
import { useContext, useState } from 'react';
import { TurnstileContext } from '../config/TurnstileProvider';
import { useTurnstile } from 'react-turnstile';
import { Gender, ISignUpDatosIniciales } from '../types/ISignUpDatosIniciales';
import { Titulo } from '../components/shared/Titulo';
import { Spinner } from '../components/shared/Spinner';
import { ErrorMessageContainer } from '../components/shared/ErrorMessageContainer';
import { axiosMessageExtractor, BackendErrorContainer } from '../config/axiosMessageExtractor';
import { useErrorMessage } from '../components/shared/useErrorMessage';
import { AxiosError } from 'axios';
import { REGISTRO_NUMEROTRAMITE_INFO, SESSION_KEY } from '../constants/data';
import { EMAIL_LABEL, EMAIL_MAX_LENGTH, EMAIL_PLACEHOLDER } from '../constants/form';
import { InfoTooltip } from '../components/shared/InfoTooltip';
import { AuthLink } from './AuthLink';

const opciones: IOption[] = [
  { id: Gender.MASCULINO, label: 'Masculino' },
  { id: Gender.FEMENINO, label: 'Femenino' },
  { id: Gender.NOBINARIO, label: 'No binario' },
];

export const SignUpUserForm = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const methods = useForm<ISignUpDatosIniciales>();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage, showError, clearError] = useErrorMessage(axiosMessageExtractor);

  const [, post] = useAxios<string, ISignUpDatosIniciales>({ url: UrlsBack.SignUpDatosIniciales, method: 'POST' }, { manual: true });

  const turnstileConfig = useContext(TurnstileContext);
  const turnstile = useTurnstile();

  const onError = (error?: AxiosError<BackendErrorContainer>) => {
    setErrorMessage(error);
    setLoading(false);
  };

  const captchaCallback = (data: ISignUpDatosIniciales) => {
    return async (captcha: string) => {
      const payload: ISignUpDatosIniciales = {
        email: data?.email,
        documentNumber: data?.documentNumber,
        procedureNumber: Number.parseInt(`${data?.procedureNumber}`),
        gender: Number.parseInt(`${data?.gender}`),
        googleToken: captcha,
      };

      await post({ data: payload })
        .then(({ data: token }) => {
          if (token) {
            localStorage.setItem('token', token);
          }
          navigate(UrlsFront.SignUpValidate, {
            state: { email: data.email, redirect: '/registro/direccion' },
          });
          setLoading(false);
        })
        .catch(onError);
    };
  };

  const onSubmit = async (data: ISignUpDatosIniciales) => {
    setLoading(true);
    clearError();
    localStorage.removeItem(SESSION_KEY);

    if (turnstile == null) {
      onError();
      return;
    }

    turnstile.reset(turnstileConfig?.widgetId);
    turnstile.execute(turnstileConfig?.widgetId, {
      callback: captchaCallback(data),
      'error-callback': onError,
    });
  };

  return (
    <>
      <Titulo>Registro</Titulo>
      <BPFormProvider methods={methods} rules={SignUpHomeRules}>
        {loading && <Spinner width={40} my={'48px'} />}

        <VStack as="form" display={loading ? 'none' : 'flex'} onSubmit={methods.handleSubmit(onSubmit)}>
          <Input label={EMAIL_LABEL} name="email" control={methods.control} placeholder={EMAIL_PLACEHOLDER} maxLength={EMAIL_MAX_LENGTH} />
          <Input label="Ingresá tu DNI" name="documentNumber" type="number" control={methods.control} placeholder="DNI" />

          <HStack alignItems="center">
            <Input label="Ingresá tu nro. de trámite de DNI" name="procedureNumber" type="number" control={methods.control} placeholder="Nro de trámite" />
            {InfoTooltip(REGISTRO_NUMEROTRAMITE_INFO, [12, 18, 20], 18, 50, false)?.({})}
          </HStack>

          <Select
            label="Seleccioná el género"
            valueAsObject={false}
            name="gender"
            control={methods.control}
            options={opciones}
            placeholder="Género como figura en tu DNI"
            defaultValue=""
          />

          <ErrorMessageContainer show={showError} errorMessage={errorMessage} mt={24} mb={16} />

          <HStack>
            <Button type="submit" variant="secondary">
              Confirmar
            </Button>
          </HStack>
        </VStack>
      </BPFormProvider>

      <AuthLink show={!loading} />
    </>
  );
};
